import React, {FC, useMemo} from 'react'
import {DocBlock, DocField, DocFieldGrid, DocNote} from './ProcurementDocumentSections'
import {parseQuotationTerms} from '../core/quotationTerms'
import ProcurementAttachmentPrintList from './ProcurementAttachmentPrintList'
import {useProcurementBrandingAssets} from './ProcurementDocumentBranding'
import {formatExchangeRateDisplay} from '../core/currencyConversion'
import MoneyDisplay from './MoneyDisplay'
import {
  readFiniteAmount,
  resolveQuotationComparisonMetadata,
  resolveQuotationCompanyCurrency,
  resolveQuotationDisplayCurrency,
  resolveSelectedLineAmounts,
} from '../core/quotationComparison'
import {
  getContextHeaderLabels,
  getEquipmentNameFromLine,
  getItemDisplayNameFromLine,
  getLinkedDefectDisplay,
  getSubcomponentNameFromLine,
  resolveRequisitionViewContext,
} from '../core/technicalItemContext'

interface QuotationDPCDocumentProps {
  rfq: any
}

type DocumentAttachment = {
  id?: number | string | null
  registryId?: number | string | null
  attachmentKey?: string | null
  fileName: string
  url?: string | null
  viewUrl?: string | null
  downloadUrl?: string | null
  sizeBytes?: number | null
  available?: boolean | null
  itemLabel?: string | null
  vendorLabel?: string | null
  quotationReference?: string | null
}

const safeNum = (v: any) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0)
const computeLineAmount = (line: any) => {
  const quantity = safeNum(line?.assignedQuantity ?? line?.quantity)
  const unitPrice = safeNum(line?.unitPrice)
  const discountPct = safeNum(line?.discountPercent)
  const gstPct = safeNum(line?.gstPercent)
  const baseAmount = quantity * unitPrice
  const discountedAmount = baseAmount * (1 - discountPct / 100)
  return discountedAmount + (discountedAmount * gstPct) / 100
}
const resolveCurrency = (quotation: any, fallback: string = 'USD') =>
  resolveQuotationDisplayCurrency(quotation, fallback)
const resolveCompanyCurrency = (quotation: any, fallback: string = 'USD') =>
  resolveQuotationCompanyCurrency(quotation, fallback) || fallback.toUpperCase()
const uniqueJoined = (values: Array<string | null | undefined>) => {
  const seen = new Set<string>()
  const result: string[] = []
  values.forEach((value) => {
    const trimmed = String(value || '').trim()
    if (!trimmed) return
    const key = trimmed.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    result.push(trimmed)
  })
  return result.length > 0 ? result.join(', ') : '-'
}
const readMeaningfulText = (...values: any[]) => {
  for (const value of values) {
    const trimmed = String(value ?? '').trim()
    if (trimmed) {
      return trimmed
    }
  }
  return null
}

const normalizeAttachment = (attachment: any, extra?: Partial<DocumentAttachment>): DocumentAttachment | null => {
  const fileName = String(attachment?.fileName || attachment?.name || '').trim()
  const url = attachment?.url || attachment?.path || null
  const id = attachment?.id ?? null
  const attachmentKey = attachment?.attachmentKey ?? (id != null ? String(id) : null)
  if (!fileName || (!url && id == null && !attachmentKey)) {
    return null
  }
  return {
    id,
    registryId: attachment?.registryId ?? id,
    attachmentKey,
    fileName,
    url,
    viewUrl: attachment?.viewUrl ?? null,
    downloadUrl: attachment?.downloadUrl ?? null,
    sizeBytes: attachment?.sizeBytes ?? attachment?.size ?? null,
    available: attachment?.available ?? true,
    itemLabel: extra?.itemLabel ?? null,
    vendorLabel: extra?.vendorLabel ?? null,
    quotationReference: extra?.quotationReference ?? null,
  }
}

const QuotationDPCDocument: FC<QuotationDPCDocumentProps> = ({rfq}) => {
  const selectedQuotations = useMemo(() => {
    if (Array.isArray(rfq?.selectedQuotations) && rfq.selectedQuotations.length > 0) {
      return rfq.selectedQuotations
    }
    if (Array.isArray(rfq?.quotations) && rfq.quotations.length > 0) {
      const selected = rfq.quotations.filter((quotation: any) => quotation?.status === 'SELECTED')
      if (selected.length > 0) return selected
      return [rfq.quotations[0]]
    }
    if (rfq?.selectedQuotation) return [rfq.selectedQuotation]
    return []
  }, [rfq])
  const invitedVendors = useMemo(() => {
    const invitationDetails = Array.isArray((rfq as any)?.vendorInvitationDetails)
      ? (((rfq as any).vendorInvitationDetails as any[]) || [])
      : []
    const map = new Map<string, {name: string; email: string; status: string; lineIds: string[]}>()
    invitationDetails.forEach((detail: any, index: number) => {
      const vendorId = Number(detail?.vendorId ?? detail?.vendor?.id ?? 0)
      const vendorName = String(detail?.vendorName || detail?.vendor?.name || '').trim()
      const vendorEmail = String(detail?.vendorEmail || detail?.vendor?.email || '').trim()
      const lineId = Number(detail?.requisitionLineId ?? detail?.requisitionLine?.id ?? 0)
      const status = String(detail?.invitationStatus || detail?.status || '').trim().toUpperCase() || 'INVITED'
      const key = vendorId > 0 ? `id-${vendorId}` : vendorName ? `name-${vendorName.toLowerCase()}` : `row-${index}`
      const existing = map.get(key)
      if (!existing) {
        map.set(key, {
          name: vendorName || (vendorId > 0 ? `Vendor ${vendorId}` : '-'),
          email: vendorEmail || '-',
          status,
          lineIds: lineId > 0 ? [String(lineId)] : [],
        })
        return
      }
      if (lineId > 0 && !existing.lineIds.includes(String(lineId))) {
        existing.lineIds.push(String(lineId))
      }
      if (!existing.email || existing.email === '-') {
        existing.email = vendorEmail || existing.email
      }
      if (existing.status === 'INVITED' && status !== 'INVITED') {
        existing.status = status
      }
    })

    if (map.size === 0 && Array.isArray((rfq as any)?.invitedVendorIds)) {
      ;(((rfq as any).invitedVendorIds as number[]) || []).forEach((vendorId) => {
        const key = `id-${vendorId}`
        if (map.has(key)) return
        map.set(key, {
          name: `Vendor ${vendorId}`,
          email: '-',
          status: 'INVITED',
          lineIds: [],
        })
      })
    }

    return Array.from(map.values())
  }, [rfq])
  const selectedQuotation = selectedQuotations[0] || null
  const parsedTerms = parseQuotationTerms(selectedQuotation?.terms)
  const lineItems = useMemo(() => {
    const reqLineById = new Map<number, any>()
    ;((rfq?.requisition as any)?.lines || []).forEach((line: any, index: number) => {
      if (line?.id != null) {
        reqLineById.set(Number(line.id), line)
      } else {
        reqLineById.set(-(index + 1), line)
      }
    })
    return selectedQuotations.flatMap((quotation: any, vendorIndex: number) =>
      (quotation?.lines || []).map((line: any, lineIndex: number) => {
        const reqLine =
          line?.requisitionLineId != null
            ? reqLineById.get(Number(line.requisitionLineId))
            : reqLineById.get(-(lineIndex + 1))
        return {
          ...line,
          __reqLine: reqLine || null,
          __lineKey: `${quotation?.id || vendorIndex}-${line?.requisitionLineId || lineIndex}`,
          __vendorName: quotation?.vendor?.name || `Vendor ${vendorIndex + 1}`,
          __vendorCode: quotation?.vendor?.code || '-',
          __currency: resolveCurrency(quotation, resolveCurrency(selectedQuotation, 'USD')),
          __comparison: resolveQuotationComparisonMetadata(quotation, quotation?.companyCurrencyCode ?? null),
        }
      })
    )
  }, [rfq?.requisition, selectedQuotation?.currency, selectedQuotation?.quotationCurrency, selectedQuotations])
  const requisitionHeaderAttachments = useMemo(
    () =>
      (((rfq?.requisition as any)?.attachments || []) as any[])
        .map((attachment: any) => normalizeAttachment(attachment))
        .filter((attachment: DocumentAttachment | null): attachment is DocumentAttachment => attachment != null),
    [rfq?.requisition]
  )
  const requisitionLineAttachments = useMemo(
    () =>
      (((rfq?.requisition as any)?.lines || []) as any[]).flatMap((line: any, index: number) => {
        const itemLabel =
          getItemDisplayNameFromLine(line, index) ||
          line?.manualItemName ||
          line?.part?.name ||
          `Line ${index + 1}`
        return ((line?.attachments || []) as any[])
          .map((attachment: any) => normalizeAttachment(attachment, {itemLabel}))
          .filter((attachment: DocumentAttachment | null): attachment is DocumentAttachment => attachment != null)
      }),
    [rfq?.requisition]
  )
  const rfqAttachments = useMemo(
    () =>
      ((rfq?.attachments || []) as any[])
        .map((attachment: any) => normalizeAttachment(attachment))
        .filter((attachment: DocumentAttachment | null): attachment is DocumentAttachment => attachment != null),
    [rfq?.attachments]
  )
  const quotationAttachments = useMemo(
    () =>
      selectedQuotations.flatMap((quotation: any, index: number) => {
        const vendorLabel = quotation?.vendor?.name || `Vendor ${index + 1}`
        const quotationReference =
          quotation?.referenceNumber ||
          quotation?.quotationReference ||
          quotation?.vendorReference ||
          quotation?.vendor?.code ||
          null
        return ((quotation?.attachments || []) as any[])
          .map((attachment: any) =>
            normalizeAttachment(attachment, {
              vendorLabel,
              quotationReference,
            })
          )
          .filter((attachment: DocumentAttachment | null): attachment is DocumentAttachment => attachment != null)
      }),
    [selectedQuotations]
  )
  const hasAnyAttachments =
    requisitionHeaderAttachments.length > 0 ||
    requisitionLineAttachments.length > 0 ||
    rfqAttachments.length > 0 ||
    quotationAttachments.length > 0
  const currency = resolveCurrency(selectedQuotation, 'USD')
  const quotationComparisons = useMemo(
    () =>
      selectedQuotations.map((quotation: any) =>
        resolveQuotationComparisonMetadata(quotation, quotation?.companyCurrencyCode ?? null)
      ),
    [selectedQuotations]
  )
  const vesselName =
    (rfq?.requisition as any)?.vessel?.fleet_name ||
    (rfq?.requisition as any)?.vesselName ||
    '-'
  const companyName =
    (rfq?.requisition as any)?.category?.cgaid?.name ||
    (rfq?.requisition as any)?.companyName ||
    '-'
  const {logoSrc: companyLogoUrl, watermarkSrc: companyWatermarkUrl, ready: brandingReady} =
    useProcurementBrandingAssets(
      (rfq as any)?.companyLogoUrl ||
        (rfq?.requisition as any)?.companyLogoUrl ||
        (rfq?.requisition as any)?.logoUrl ||
        (rfq?.requisition as any)?.company?.logoUrl ||
        (rfq?.requisition as any)?.category?.caid?.logoUrl ||
        (rfq?.requisition as any)?.category?.cgaid?.logoUrl,
      (rfq as any)?.companyWatermarkUrl ||
        (rfq?.requisition as any)?.companyWatermarkUrl ||
        (rfq?.requisition as any)?.watermarkUrl ||
        (rfq?.requisition as any)?.company?.watermarkUrl ||
        (rfq?.requisition as any)?.category?.caid?.watermarkUrl ||
        (rfq?.requisition as any)?.category?.cgaid?.watermarkUrl,
      (rfq?.requisition as any)?.category?.cgaid
    )
  const requisitionAny = (rfq?.requisition as any) || {}
  const departmentName =
    requisitionAny?.departmentName ||
    requisitionAny?.inventoryItemHead?.name ||
    requisitionAny?.inventoryItemHead ||
    '-'
  const subDepartmentName =
    requisitionAny?.subDepartmentName ||
    requisitionAny?.inventoryItemSubHead?.name ||
    requisitionAny?.inventoryItemSubHead ||
    '-'
  const equipmentSummary = uniqueJoined(
    lineItems.map((line: any) =>
      getEquipmentNameFromLine({
        ...line,
        ...(line?.__reqLine || {}),
        subcomponent: line?.subcomponent || line?.__reqLine?.subcomponent,
        part: line?.part || line?.__reqLine?.part,
      })
    )
  )
  const subcomponentSummary = uniqueJoined(
    lineItems.map((line: any) =>
      getSubcomponentNameFromLine({
        ...line,
        ...(line?.__reqLine || {}),
        subcomponent: line?.subcomponent || line?.__reqLine?.subcomponent,
        part: line?.part || line?.__reqLine?.part,
      })
    )
  )
  const context = resolveRequisitionViewContext(
    (rfq?.requisition as any)?.requestFor || rfq?.requestFor,
    requisitionAny?.category?.name
  )
  const isTechnical = context === 'TECHNICAL'
  const {primary: contextPrimaryLabel, secondary: contextSecondaryLabel} =
    getContextHeaderLabels(context)
  const categoryName = requisitionAny?.category?.name || requisitionAny?.categoryName || '-'
  const linkedDefectDisplay = getLinkedDefectDisplay(requisitionAny) || '-'
  const subCategoryName =
    requisitionAny?.inventoryItemSubCategory?.name ||
    requisitionAny?.inventoryItemSubCategoryName ||
    requisitionAny?.inventoryItemSubHead?.name ||
    requisitionAny?.inventoryItemSubHeadName ||
    requisitionAny?.subDepartmentName ||
    '-'
  const contextPrimaryValue = isTechnical
    ? equipmentSummary
    : context === 'SERVICES'
    ? departmentName
    : categoryName
  const contextSecondaryValue = isTechnical
    ? subcomponentSummary
    : context === 'SERVICES'
    ? subDepartmentName
    : subCategoryName
  const freightCost = selectedQuotations.reduce(
    (sum: number, quotation: any) => sum + safeNum(quotation?.freightCost ?? 0),
    0
  ) || safeNum(parsedTerms.freightCost)
  const packagingCost = selectedQuotations.reduce(
    (sum: number, quotation: any) => sum + safeNum(quotation?.packagingCost ?? 0),
    0
  ) || safeNum(parsedTerms.packagingCost)
  const truckCost = selectedQuotations.reduce(
    (sum: number, quotation: any) => sum + safeNum(quotation?.truckCost ?? 0),
    0
  ) || safeNum(parsedTerms.truckCost)
  const bargeCost = selectedQuotations.reduce(
    (sum: number, quotation: any) => sum + safeNum(quotation?.bargeCost ?? 0),
    0
  ) || safeNum(parsedTerms.bargeCost)
  const otherCost = selectedQuotations.reduce(
    (sum: number, quotation: any) => sum + safeNum(quotation?.otherCost ?? 0),
    0
  ) || safeNum(parsedTerms.otherCost)
  const vatOnLogistics = selectedQuotations.reduce(
    (sum: number, quotation: any) => sum + safeNum(quotation?.vatOnLogistics ?? 0),
    0
  ) || safeNum(parsedTerms.vatOnLogistics)
  const discount = selectedQuotations.reduce(
    (sum: number, quotation: any) => sum + safeNum(quotation?.totalDiscount ?? 0),
    0
  )
  const vendorPartsOfOriginValue =
    selectedQuotations
      .map((quotation: any, idx: number) => {
        const vendorName = quotation?.vendor?.name || `Vendor ${idx + 1}`
        const origin = String(quotation?.partsOfOrigin || '').trim() || '-'
        return `${vendorName}: ${origin}`
      })
      .join(' | ') || '-'
  const vendorCompanyEquivalentValue =
    selectedQuotations
      .map((quotation: any, idx: number) => {
        const vendorName = quotation?.vendor?.name || `Vendor ${idx + 1}`
        const comparison = quotationComparisons[idx]
        const companyEquivalentLabel =
          comparison?.comparableInCompanyCurrency && comparison?.companyAmount != null
            ? `${comparison.companyCurrency} ${safeNum(comparison.companyAmount).toFixed(2)}`
            : 'Company currency conversion unavailable'
        return `${vendorName}: ${companyEquivalentLabel}`
      })
      .join(' | ') || '-'
  const vendorExchangeRateValue =
    selectedQuotations
      .map((quotation: any, idx: number) => {
        const vendorName = quotation?.vendor?.name || `Vendor ${idx + 1}`
        const quoteCurrency = resolveCurrency(quotation, currency)
        const companyCurrency = resolveCompanyCurrency(quotation, currency)
        const display =
          quotation?.exchangeRateDisplay ||
          formatExchangeRateDisplay(quoteCurrency, quotation?.exchangeRate, companyCurrency)
        return `${vendorName}: ${display}`
      })
      .join(' | ') || '-'
  const partsOfOriginValue =
    Array.from(
      new Set(
        selectedQuotations
          .map((quotation: any) => String(quotation?.partsOfOrigin || '').trim())
          .filter(Boolean)
      )
    ).join(', ') ||
    parsedTerms.partsOfOrigin ||
    '-'
  const companyEquivalentValue = quotationComparisons.reduce(
    (sum: number, comparison) => sum + safeNum(comparison.companyAmount ?? 0),
    0
  )
  const hasComparableCompanyEquivalent = quotationComparisons.some(
    (comparison) => comparison.comparableInCompanyCurrency && comparison.companyAmount != null
  )
  const remittanceInstructions =
    readMeaningfulText(selectedQuotation?.remittanceInstructions, selectedQuotation?.remittanceDetails)
  const bankDetails = readMeaningfulText(selectedQuotation?.bankDetails, parsedTerms.bankDetails)
  const quotationNotes = readMeaningfulText(parsedTerms.overallRemarks, parsedTerms.remainingNotes, selectedQuotation?.terms)
  const standardTermsAndConditions = readMeaningfulText(
    selectedQuotation?.standardTermsAndConditions,
    rfq?.standardTermsAndConditions
  )
  const hasRemittanceSection = Boolean(bankDetails || remittanceInstructions)

  const computedLineTotal = useMemo(
    () => lineItems.reduce((sum: number, ln: any) => sum + computeLineAmount(ln), 0),
    [lineItems]
  )
  const logisticsTotal = safeNum((rfq as any)?.logisticsTotal ?? freightCost + packagingCost + truckCost + bargeCost + otherCost + vatOnLogistics)
  const computedTotalBeforeDiscount = computedLineTotal + freightCost + packagingCost + truckCost + bargeCost + otherCost + vatOnLogistics
  const hasAssignedLines = lineItems.some((line: any) => safeNum(line?.assignedQuantity) > 0)
  const total = hasAssignedLines
    ? safeNum(computedTotalBeforeDiscount)
    : selectedQuotations.reduce((sum: number, quotation: any) => sum + safeNum(quotation?.totalAmount ?? 0), 0) ||
      safeNum(computedTotalBeforeDiscount)
  const grandTotal = total - discount
  const rootClassName = `procurement-doc dpc-doc${companyWatermarkUrl ? ' procurement-has-watermark' : ''}`

  return (
    <div className={rootClassName} data-export-ready={brandingReady ? 'true' : 'false'}>
      {companyWatermarkUrl ? (
        <div className='procurement-doc-watermark' aria-hidden='true'>
          <img
            src={companyWatermarkUrl}
            alt=''
            className='procurement-doc-watermark-image'
            crossOrigin='anonymous'
            data-pdf-watermark-source='true'
          />
        </div>
      ) : null}
      <div className='procurement-doc-content'>
        {companyLogoUrl ? (
          <div className='procurement-doc-brand-header mb-3'>
            <img
              src={companyLogoUrl}
              alt='Company Logo'
              className='procurement-doc-brand-logo'
              crossOrigin='anonymous'
              data-pdf-logo='true'
            />
          </div>
        ) : null}

      {false && (
        <>
      {selectedQuotations.length > 1 && (
        <DocBlock title='Selected Vendor Quotations'>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle doc-tight'>
              <thead className='table-light'>
                <tr>
                  <th>Vendor</th>
                  <th style={{width: '150px'}}>Assigned Qty</th>
                  <th style={{width: '180px'}}>Total Amount</th>
                  <th style={{width: '220px'}}>Company Amount</th>
                  <th style={{width: '180px'}}>Promised Delivery</th>
                </tr>
              </thead>
              <tbody>
                {selectedQuotations.map((quotation: any, idx: number) => {
                  const comparison = quotationComparisons[idx]
                  const assignedQty = (quotation?.lines || []).reduce(
                    (sum: number, line: any) => sum + safeNum(line?.assignedQuantity ?? 0),
                    0
                  )
                  const lineQtyFallback = (quotation?.lines || []).reduce(
                    (sum: number, line: any) => sum + safeNum(line?.quantity ?? 0),
                    0
                  )
                  const quoteCurrency = resolveCurrency(quotation, currency)
                  return (
                    <tr key={`selected-vendor-${quotation?.id || idx}`}>
                      <td>{quotation?.vendor?.name || 'N/A'}</td>
                      <td>{assignedQty > 0 ? assignedQty : lineQtyFallback}</td>
                      <td>
                        {quoteCurrency} {safeNum(quotation?.totalAmount).toFixed(2)}
                      </td>
                      <td>
                        {comparison?.comparableInCompanyCurrency && comparison?.companyAmount != null ? (
                          <MoneyDisplay
                            compact
                            primaryAmount={comparison.companyAmount}
                            primaryCurrency={comparison.companyCurrency}
                            secondaryAmount={comparison.originalAmount}
                            secondaryCurrency={comparison.originalCurrency}
                            exchangeRateDisplay={comparison.exchangeRateDisplay}
                            sameCurrency={comparison.originalCurrency === comparison.companyCurrency}
                          />
                        ) : (
                          <span className='text-warning'>Company currency conversion unavailable</span>
                        )}
                      </td>
                      <td>{quotation?.promisedDeliveryDate || '-'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </DocBlock>
      )}
        </>
      )}

      <DocBlock title='Invited Vendors'>
        {invitedVendors.length === 0 ? (
          <div className='text-muted'>No invited vendor records found.</div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-bordered align-middle doc-tight'>
              <thead className='table-light'>
                <tr>
                  <th>Vendor</th>
                  <th style={{width: '220px'}}>Email</th>
                  <th style={{width: '180px'}}>Invitation Status</th>
                  <th style={{width: '200px'}}>Requisition Line(s)</th>
                </tr>
              </thead>
              <tbody>
                {invitedVendors.map((vendor, index) => (
                  <tr key={`invited-vendor-${index}`}>
                    <td>{vendor.name || '-'}</td>
                    <td>{vendor.email || '-'}</td>
                    <td>{vendor.status || 'INVITED'}</td>
                    <td>{vendor.lineIds.length > 0 ? vendor.lineIds.join(', ') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DocBlock>

      <DocBlock title='Quotation Header'>
        <DocFieldGrid columns={4}>
          <DocField label='Date' value={selectedQuotation?.promisedDeliveryDate || parsedTerms.validity || '-'} />
          <DocField label='Vendor Reference' value={parsedTerms.ourRef || rfq?.title} />
          <DocField label='Vendor Ref' value={selectedQuotation?.vendor?.code || '-'} />
          <DocField
            label='Company Reference'
            value={parsedTerms.yourRef || rfq?.requisition?.requisition_code || rfq?.requisition?.title}
          />
          <DocField label='Linked Defect' value={linkedDefectDisplay} />
          <DocField label='Yard' value={(rfq?.requisition as any)?.yard || '-'} />
          <DocField label='Vessel' value={vesselName} />
          <DocField label='Company' value={companyName} />
          <DocField label={contextPrimaryLabel} value={contextPrimaryValue} />
          <DocField label={contextSecondaryLabel} value={contextSecondaryValue} />
          <DocField
            label='Terms Of Delivery'
            value={parsedTerms.termsOfDelivery || selectedQuotation?.locationOfDelivery || '-'}
          />
          <DocField
            label='Terms Of Payment'
            value={selectedQuotation?.termsOfPayment || parsedTerms.termsOfPayment || '-'}
          />
          <DocField
            label='Billing Address'
            value={
              selectedQuotation?.billingAddressLabel ||
              selectedQuotation?.billingAddressDisplay ||
              '-'
            }
          />
          <DocField
            label='Shipping Address'
            value={
              selectedQuotation?.shippingAddressLabel ||
              selectedQuotation?.shippingAddressDisplay ||
              '-'
            }
          />
          <DocField label='Validity' value={selectedQuotation?.validity || parsedTerms.validity || '-'} />
          <DocField
            label='Parts Of Origin'
            value={selectedQuotations.length > 1 ? vendorPartsOfOriginValue : partsOfOriginValue}
          />
          <DocField label='Currency' value={currency} />
          <DocField
            label='Exchange Rate'
            value={
              selectedQuotations.length > 1
                ? vendorExchangeRateValue
                : selectedQuotation?.exchangeRateDisplay ||
                  formatExchangeRateDisplay(
                    currency,
                    selectedQuotation?.exchangeRate,
                    resolveCompanyCurrency(selectedQuotation, currency)
                  )
            }
          />
          <DocField
            label='Company Equivalent'
            value={
              selectedQuotations.length > 1
                ? vendorCompanyEquivalentValue
                : hasComparableCompanyEquivalent
                  ? `${quotationComparisons[0]?.companyCurrency || resolveCompanyCurrency(selectedQuotation, currency)} ${safeNum(companyEquivalentValue).toFixed(2)}`
                  : 'Company currency conversion unavailable'
            }
          />
          <DocField
            label='Location Of Delivery'
            value={selectedQuotation?.locationOfDelivery || parsedTerms.locationOfDelivery || '-'}
          />
        </DocFieldGrid>
      </DocBlock>

      {false && (
        <>
      <DocBlock title='Quotation Line Items'>
        {lineItems.length === 0 ? (
          <div className='text-muted'>No quoted line items available.</div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-bordered align-middle doc-tight procurement-print-table'>
              <thead className='table-light'>
                <tr>
                  <th style={{width: '56px'}}>No</th>
                  <th style={{width: '160px'}}>Vendor</th>
                  <th>Description</th>
                  <th style={{width: '80px'}}>Qty</th>
                  <th style={{width: '80px'}}>Unit</th>
                  <th style={{width: '120px'}}>Unit Price</th>
                  <th style={{width: '120px'}}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((ln: any, idx: number) => {
                  const lineCurrency = ln.__currency || currency
                  const mergedLine = {
                    ...ln,
                    ...(ln?.__reqLine || {}),
                    subcomponent: ln?.subcomponent || ln?.__reqLine?.subcomponent,
                    part: ln?.part || ln?.__reqLine?.part,
                  }
                  const partName = mergedLine?.part?.name || '-'
                  const partNo = mergedLine?.part?.partNo || mergedLine?.partNo || mergedLine?.partCode || '-'
                  const drawingNo = mergedLine?.part?.drawNo || mergedLine?.drawingNo || '-'
                  const quantity = safeNum(ln.assignedQuantity ?? ln.quantity)
                  const unitPrice = safeNum(ln.unitPrice)
                  const discountPct = safeNum(ln.discountPercent)
                  const gstPct = safeNum(ln.gstPercent)
                  const base = quantity * unitPrice
                  const afterDisc = base - (base * discountPct) / 100
                  const amount = afterDisc + (afterDisc * gstPct) / 100
                  const selectedAmounts = resolveSelectedLineAmounts(ln, quantity, ln.__comparison)
                  return (
                    <React.Fragment key={ln.__lineKey || `q-line-${idx}`}>
                      <tr className='procurement-avoid-break'>
                        <td>{idx + 1}</td>
                        <td>{ln.__vendorName || '-'}</td>
                        <td>
                          {getItemDisplayNameFromLine(mergedLine, idx)}
                          <div className='procurement-line-detail'>
                            {isTechnical
                              ? `Part: ${partName} | Part No: ${partNo} | Drawing: ${drawingNo}`
                              : `${contextPrimaryLabel}: ${contextPrimaryValue} | ${contextSecondaryLabel}: ${contextSecondaryValue}`}
                          </div>
                          {isTechnical ? (
                            <div className='procurement-line-detail'>
                              {contextPrimaryLabel}: {getEquipmentNameFromLine(mergedLine) || '-'} |{' '}
                              {contextSecondaryLabel}: {getSubcomponentNameFromLine(mergedLine) || '-'}
                            </div>
                          ) : null}
                          <div className='procurement-line-detail'>
                            D/T: {ln.deliveryTimeDays ?? '-'} | Discount: {discountPct.toFixed(2)}% | GST:{' '}
                            {gstPct.toFixed(2)}%
                          </div>
                          {(ln.remarks || ln.stockNote) && (
                            <div className='procurement-line-note'>
                              <span className='text-muted fw-semibold'>Remarks/Stock:</span>{' '}
                              {ln.remarks || ln.stockNote}
                            </div>
                          )}
                          {ln.notes && (
                            <div className='procurement-line-note'>
                              <span className='text-muted fw-semibold'>Line Note:</span> {ln.notes}
                            </div>
                          )}
                        </td>
                        <td>{quantity || '-'}</td>
                        <td>{ln.unit || '-'}</td>
                        <td>
                          {lineCurrency} {unitPrice.toFixed(2)}
                        </td>
                        <td>
                          <MoneyDisplay
                            compact
                            primaryAmount={selectedAmounts.selectedCompanyAmount}
                            primaryCurrency={ln.__comparison?.companyCurrency || lineCurrency}
                            secondaryAmount={selectedAmounts.selectedOriginalAmount ?? amount}
                            secondaryCurrency={lineCurrency}
                            exchangeRateDisplay={ln.__comparison?.exchangeRateDisplay}
                            sameCurrency={lineCurrency === ln.__comparison?.companyCurrency}
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </DocBlock>

      <DocBlock title='Commercial Summary'>
        <div className='row g-4'>
          <div className='col-12'>
            <table className='table table-sm table-borderless mb-0'>
              <tbody>
                <tr>
                  <td className='text-muted'>Subtotal (Sum of Line Amounts)</td>
                  <td className='text-end fw-semibold'>
                    {currency} {computedLineTotal.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className='text-muted'>Trucking/Freight Cost</td>
                  <td className='text-end fw-semibold'>
                    {currency} {freightCost.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className='text-muted'>Pkg & Handling Cost</td>
                  <td className='text-end fw-semibold'>
                    {currency} {packagingCost.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className='text-muted'>Truck Cost</td>
                  <td className='text-end fw-semibold'>
                    {currency} {truckCost.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className='text-muted'>Barge/Workboat Cost</td>
                  <td className='text-end fw-semibold'>
                    {currency} {bargeCost.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className='text-muted'>Other Cost</td>
                  <td className='text-end fw-semibold'>
                    {currency} {otherCost.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className='text-muted'>VAT/GST on Logistic/Other</td>
                  <td className='text-end fw-semibold'>
                    {currency} {vatOnLogistics.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className='text-muted'>Logistics Total</td>
                  <td className='text-end fw-semibold'>
                    {currency} {logisticsTotal.toFixed(2)}
                  </td>
                </tr>
                <tr>
                  <td className='text-muted'>Discount / D/C</td>
                  <td className='text-end fw-semibold'>
                    {currency} {safeNum(discount).toFixed(2)}
                  </td>
                </tr>
                <tr className='border-top'>
                  <td className='fw-bold'>Grand Total</td>
                  <td className='text-end fw-bold'>
                    {currency} {safeNum(grandTotal).toFixed(2)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </DocBlock>

      {hasRemittanceSection ? (
        <DocBlock title='Remittance / Bank Details'>
          <DocFieldGrid columns={2}>
            <DocNote title='Bank Details'>{bankDetails}</DocNote>
            <DocNote title='Remittance Instructions'>{remittanceInstructions}</DocNote>
          </DocFieldGrid>
        </DocBlock>
      ) : null}

      <DocBlock title='Quotation Notes'>
        {quotationNotes ? (
          <div className='mb-4'>
            <DocNote title='Special Conditions / Notes'>{quotationNotes}</DocNote>
          </div>
        ) : null}
        <DocFieldGrid columns={2}>
          <DocNote title='D/T Meaning Note'>
            {parsedTerms.deliveryMeaningNote || 'D/T = Delivery time in calendar days from PO confirmation.'}
          </DocNote>
          <DocNote title='Asbestos-Free Note'>
            {parsedTerms.asbestosFreeNote || 'All supplied parts/materials are asbestos-free unless explicitly declared.'}
          </DocNote>
        </DocFieldGrid>
      </DocBlock>

      {standardTermsAndConditions ? (
        <DocBlock title='Terms & Conditions'>
          <DocNote title='Terms & Conditions'>{standardTermsAndConditions}</DocNote>
        </DocBlock>
      ) : null}

      <DocBlock title='Attachments'>
        {!hasAnyAttachments ? (
          <div className='text-muted'>No attachments available.</div>
        ) : (
          <div className='d-flex flex-column gap-4'>
            {requisitionHeaderAttachments.length > 0 && (
              <div>
                <div className='text-muted fw-semibold fs-8 text-uppercase mb-2'>Requisition Attachments</div>
                <ProcurementAttachmentPrintList attachments={requisitionHeaderAttachments} />
              </div>
            )}
            {requisitionLineAttachments.length > 0 && (
              <div>
                <div className='text-muted fw-semibold fs-8 text-uppercase mb-2'>Line Item Attachments</div>
                <ProcurementAttachmentPrintList
                  attachments={requisitionLineAttachments.map((attachment) => ({
                    ...attachment,
                    fileName: attachment.itemLabel
                      ? `${attachment.itemLabel}: ${attachment.fileName}`
                      : attachment.fileName,
                  }))}
                />
              </div>
            )}
            {rfqAttachments.length > 0 && (
              <div>
                <div className='text-muted fw-semibold fs-8 text-uppercase mb-2'>RFQ Attachments</div>
                <ProcurementAttachmentPrintList attachments={rfqAttachments} />
              </div>
            )}
            {quotationAttachments.length > 0 && (
              <div>
                <div className='text-muted fw-semibold fs-8 text-uppercase mb-2'>Vendor Quotation Attachments</div>
                <ProcurementAttachmentPrintList
                  attachments={quotationAttachments.map((attachment: any) => ({
                    ...attachment,
                    fileName:
                      [attachment.vendorLabel, attachment.quotationReference, attachment.fileName]
                        .filter(Boolean)
                        .join(' - ') || attachment.fileName,
                  }))}
                />
              </div>
            )}
          </div>
        )}
      </DocBlock>
        </>
      )}
      </div>
    </div>
  )
}

export default QuotationDPCDocument
